import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useRef } from 'react';
import {
  useForm as _useForm,
  type UseFormProps as _UseFormProps,
  type FieldValues,
  type UseFormHandleSubmit,
  type UseFormProps,
} from 'react-hook-form';
import { type ZodType } from 'zod';

export function useForm<TOut extends FieldValues, TIn extends FieldValues>(
  props: Omit<UseFormProps<TIn, unknown, TOut>, 'resolver'> & {
    validationSchema?: ZodType<TOut, TIn>;
  }
) {
  const form = _useForm<TIn, unknown, TOut>({
    mode: props.mode ?? 'onChange',
    reValidateMode: props.reValidateMode ?? 'onChange',
    resolver: props.validationSchema ? zodResolver(props.validationSchema) : undefined,
    ...props,
  });

  const submittedForm = useRef<HTMLFormElement | null>(null);
  const handledSubmitCount = useRef(0);
  const { submitCount, errors } = form.formState;
  const rhfHandleSubmit = form.handleSubmit;
  const handleSubmit: UseFormHandleSubmit<TIn, TOut> = useCallback(
    (onValid, onInvalid) => {
      const submit = rhfHandleSubmit(onValid, onInvalid);
      return (event) => {
        // Capture before RHF awaits validation: React clears currentTarget afterward.
        const target = event?.currentTarget ?? event?.target ?? document.activeElement;
        submittedForm.current = target instanceof Element ? target.closest('form') : null;
        return submit(event);
      };
    },
    [rhfHandleSubmit]
  );

  useEffect(() => {
    if (submitCount === handledSubmitCount.current) return;
    if (props.shouldFocusError === false || Object.keys(errors).length === 0) {
      handledSubmitCount.current = submitCount;
      return;
    }

    // WebKit may ignore RHF's focus after async validation. Reveal the entire field,
    // including its error, even for controls without a focusable RHF ref. Wait until
    // the error is rendered and RHF's focus attempt has finished; typing never scrolls.
    const element = submittedForm.current;
    const frame = requestAnimationFrame(() => {
      handledSubmitCount.current = submitCount;
      if (!element?.isConnected) return;
      element
        .querySelector('[data-invalid], [aria-invalid="true"]')
        ?.scrollIntoView({ block: 'center', behavior: 'instant' });
    });
    return () => cancelAnimationFrame(frame);
  }, [submitCount, errors, props.shouldFocusError]);

  // Back and sheet dismissal protect the form's own dialog, without blocking nested menus.
  return {
    ...form,
    handleSubmit,
    nativeBackProps: {
      // RHF's successful submit means the handler returned, not that the server saved.
      // Only the form owner can clear the draft, by resetting after an actual save.
      'data-native-back-dirty': form.formState.isDirty,
      'data-native-back-busy': form.formState.isSubmitting,
    },
  };
}
